import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/ui/Modal'
import { FormField } from '@/components/ui/FormField'
import { apiService } from '@/services/api'
import type { Client } from '@/types'

const addClientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email'),
  phoneNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other', 'prefer-not-to-say']).optional(),
  preferredContactMethod: z.enum(['email', 'sms', 'app'])
})

type AddClientFormData = z.infer<typeof addClientSchema>

interface AddClientModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (client: Client) => void
}

export function AddClientModal({ isOpen, onClose, onSave }: AddClientModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<AddClientFormData>({
    resolver: zodResolver(addClientSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      gender: undefined,
      preferredContactMethod: 'app'
    }
  })

  const handleSave = async (data: AddClientFormData) => {
    setIsLoading(true)
    
    try {
      // Prepare client data for API (matching backend CreateClientSchema)
      const clientData = {
        firstName: data.name.split(' ')[0] || data.name,
        lastName: data.name.split(' ').slice(1).join(' ') || 'N/A',
        email: data.email,
        tier: 'BASIC' as const,
        services: {
          training: true,
          nutrition: false,
          habitCoaching: false
        },
        goals: [],
        healthConditions: [],
        notes: data.phoneNumber ? `Phone: ${data.phoneNumber}` : undefined
        // assignedCoachId will be set automatically by backend based on authenticated user
      }

      console.log('🚀 Creating client with data:', clientData)
      
      // Call real API
      const response = await apiService.createClient(clientData)
      console.log('✅ Client created successfully:', response)
      
      // Transform API response to match frontend Client type
      const apiClient = (response as any).data || response
      const newClient: Client = {
        id: apiClient.id,
        userId: apiClient.userId,
        name: `${apiClient.user?.firstName || ''} ${apiClient.user?.lastName || ''}`.trim() || apiClient.user?.email || data.name,
        email: apiClient.user?.email || data.email,
        phoneNumber: apiClient.user?.phoneNumber || data.phoneNumber,
        profilePhoto: apiClient.user?.profilePicture,
        dateOfBirth: apiClient.user?.dateOfBirth ? new Date(apiClient.user.dateOfBirth) : undefined,
        gender: data.gender,
        
        // Coaching relationship
        coachId: apiClient.assignedCoachId || 'unknown',
        assignedDate: new Date(apiClient.createdAt),
        status: 'onboarding',
        
        // Default goals and targets
        goals: apiClient.goals || [],
        currentProgram: undefined,
        
        // Empty measurements and progress photos initially
        measurements: [],
        progressPhotos: [],
        
        // Communication defaults
        lastActivity: new Date(),
        responseRate: 0,
        preferredContactMethod: data.preferredContactMethod,
        
        // Initial quick stats
        quickStats: {
          weeklyCompliance: 0,
          progressTrend: 'stable',
          engagementLevel: 'medium',
          daysWithCoach: Math.floor((Date.now() - new Date(apiClient.createdAt).getTime()) / (1000 * 60 * 60 * 24)),
          currentStreak: 0,
          totalWorkouts: 0,
          averageRating: 0
        },
        
        createdAt: new Date(apiClient.createdAt),
        updatedAt: new Date(apiClient.updatedAt)
      }
      
      onSave(newClient)
      reset()
    } catch (error: any) {
      console.error('❌ Failed to add client:', error)
      console.error('Error details:', error.response?.data || error.message)
      // TODO: Show user-friendly error message
      alert(`Failed to create client: ${error.response?.data?.message || error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Client"
      size="lg"
    >
      <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Add a new client to start their fitness journey. You can update their goals and program details after creation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Full Name"
            htmlFor="name"
            required
            error={errors.name?.message}
          >
            <input
              id="name"
              type="text"
              className="coach-input"
              placeholder="John Doe"
              {...register('name')}
            />
          </FormField>

          <FormField
            label="Email Address"
            htmlFor="email"
            required
            error={errors.email?.message}
          >
            <input
              id="email"
              type="email"
              className="coach-input"
              placeholder="john.doe@email.com"
              {...register('email')}
            />
          </FormField>

          <FormField
            label="Phone Number"
            htmlFor="phoneNumber"
            error={errors.phoneNumber?.message}
          >
            <input
              id="phoneNumber"
              type="tel"
              className="coach-input"
              placeholder="+1-555-0123"
              {...register('phoneNumber')}
            />
          </FormField>

          <FormField
            label="Date of Birth"
            htmlFor="dateOfBirth"
            error={errors.dateOfBirth?.message}
          >
            <input
              id="dateOfBirth"
              type="date"
              className="coach-input"
              {...register('dateOfBirth')}
            />
          </FormField>

          <FormField
            label="Gender"
            htmlFor="gender"
            error={errors.gender?.message}
          >
            <select
              id="gender"
              className="coach-input"
              {...register('gender')}
            >
              <option value="">Select gender (optional)</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </FormField>

          <FormField
            label="Preferred Contact Method"
            htmlFor="preferredContactMethod"
            required
            error={errors.preferredContactMethod?.message}
          >
            <select
              id="preferredContactMethod"
              className="coach-input"
              {...register('preferredContactMethod')}
            >
              <option value="app">In-App Messages</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </FormField>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">What happens next?</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Client will be created with "Onboarding" status</li>
            <li>• You can set up their goals and assign a program</li>
            <li>• Client will receive an invitation to join the platform</li>
          </ul>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="coach-button-secondary px-4 py-2"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="coach-button-primary px-4 py-2"
            disabled={isLoading}
          >
            {isLoading ? 'Adding Client...' : 'Add Client'}
          </button>
        </div>
      </form>
    </Modal>
  )
}